import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Alert,
    AutoComplete,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Typography,
    message,
} from 'antd';
import { ReloadOutlined, PlayCircleOutlined, CodeOutlined, UserOutlined, BankOutlined } from '@ant-design/icons';
import { ReactFlowProvider } from 'reactflow';

import DynamicWorkflowForm from '@/modules/action-flow/runtime/components/dynamic-form/DynamicWorkflowForm';
import ApprovalDecision from '@/modules/action-flow/runtime/components/requests/ApprovalDecision';
import LoanScheduleView from '@/modules/loans/components/LoanScheduleView';
import LoanGuarantorView from '@/modules/loans/components/LoanGuarantorView';
import LoanDocumentsView from '@/modules/loans/components/LoanDocumentsView';
import LoanOfficerRecommendationView from '@/modules/loans/components/LoanOfficerRecommendationView';
import LoanApplicationFormView from '@/modules/loans/components/LoanApplicationFormView';
import DeductionFormView from '@/modules/loans/components/DeductionFormView';
import DocumentUploadView from '@/modules/loans/components/DocumentUploadView';
import OfficerDeductionFormGenerationView from '@/modules/loans/components/OfficerDeductionFormGenerationView';
import LoanDetailsViewWithProvider from '@/modules/loans/components/LoanDetailsViewWithProvider';
import LoanOfficerViewWithProvider from '@/modules/loans/components/LoanOfficerViewWithProvider';
import LoanSupervisorReviewViewWithProvider from '@/modules/loans/components/LoanSupervisorReviewViewWithProvider';
import {
    ESSLoanDetailsView,
    ESSEmployeeInfoView,
    ESSFinancialInfoView,
    ESSQualificationAdviceView,
    ESSTakeoverInfoView,
    ESSLoanSummaryView,
    ESSDebugView,
} from '@/modules/loans/components/ess';

import { MOCK_ACTION_FLOW_DATA } from '../data/mockLoanData';
import { useFlowSimulatorData } from '../hooks/useFlowSimulatorData';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ---------------------------------------------------------------------------
// Default node config — mirrors docs/sample-node-based-config.json
// ---------------------------------------------------------------------------
const DEFAULT_NODE_CONFIG = {
    defaultView: {
        steps: [
            {
                title: 'Default Request Information',
                description: 'General request information for all users',
                layout: { columns: 1 },
                fields: [],
                customComponents: [
                    {
                        type: 'customComponent',
                        componentName: 'ApprovalDecision',
                        props: { required: true, fromContext: true },
                        key: 'approvalDecision',
                    },
                ],
            },
        ],
    },
    groupViews: {},
    nodeViews: {
        LOAN_OFFICER: {
            steps: [
                {
                    title: 'Loan Officer Review',
                    description: 'Review and update loan details as a loan officer',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanOfficerView', props: { fromContext: true } }],
                },
                {
                    title: 'Application Documents',
                    description: 'Review the uploaded loan application documents',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanDocumentsView', props: { fromContext: true } }],
                },
                {
                    title: 'Loan Schedule',
                    description: 'Review the loan repayment schedule',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanScheduleView', props: { fromContext: true, showRiskAssessment: true, useRecommendedValues: true } }],
                },
                {
                    title: 'Generate Deduction Form',
                    description: 'Fill in officer details, generate, download, and upload the official deduction form.',
                    customComponents: [{ type: 'customComponent', componentName: 'OfficerDeductionFormGenerationView', props: { fromContext: true } }],
                },
                {
                    title: 'Decision',
                    customComponents: [{ type: 'customComponent', componentName: 'ApprovalDecision', props: { required: true, fromContext: true }, key: 'approvalDecision' }],
                },
            ],
        },
        MEMBER_DEDUCTION_FORM: {
            steps: [
                {
                    title: 'Loan Details Review',
                    description: 'Review the recommended loan details',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanOfficerRecommendationView', props: { fromContext: true, readOnly: true } }],
                },
                {
                    title: 'Download Deduction Form',
                    description: 'Download the official deduction form prepared by the loan officer.',
                    customComponents: [{ type: 'customComponent', componentName: 'DeductionFormView', props: { fromContext: true } }],
                },
                {
                    title: 'Upload Signed Deduction Form',
                    description: 'Upload the signed deduction form document returned by your employer.',
                    customComponents: [{ type: 'customComponent', componentName: 'DocumentUploadView', props: { fromContext: true, documentType: 'SIGNED_DEDUCTION_FORM', required: true } }],
                },
                {
                    title: 'Maker Decision',
                    customComponents: [{ type: 'customComponent', componentName: 'ApprovalDecision', props: { required: true, fromContext: true }, key: 'approvalDecision' }],
                },
            ],
        },
        MEMBER_APPLICATION_FORM: {
            steps: [
                {
                    title: 'Loan Details Review',
                    description: 'Review the recommended loan details before generating the application form.',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanOfficerRecommendationView', props: { fromContext: true, readOnly: true } }],
                },
                {
                    title: 'Preview Application Form',
                    description: 'Preview the generated loan application form using the current simulator data.',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanApplicationFormView', props: { fromContext: true } }],
                },
                {
                    title: 'Maker Decision',
                    customComponents: [{ type: 'customComponent', componentName: 'ApprovalDecision', props: { required: true, fromContext: true }, key: 'approvalDecision' }],
                },
            ],
        },
        GUARANTORS: {
            steps: [
                {
                    title: 'Guarantor Information',
                    description: 'Review loan details you are being asked to guarantee',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanGuarantorView', props: { fromContext: true } }],
                },
                {
                    title: 'Guarantor Decision',
                    description: 'Make your decision on whether to guarantee this loan',
                    customComponents: [{ type: 'customComponent', componentName: 'ApprovalDecision', props: { required: true, fromContext: true }, key: 'approvalDecision' }],
                },
            ],
        },
        LOAN_SUPERVISOR: {
            steps: [
                {
                    title: 'Supervisor Review',
                    description: 'Review the loan application as a supervisor',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanSupervisorReviewView', props: { fromContext: true } }],
                },
                {
                    title: 'Decision',
                    customComponents: [{ type: 'customComponent', componentName: 'ApprovalDecision', props: { required: true, fromContext: true }, key: 'approvalDecision' }],
                },
            ],
        },
        APPROVER: {
            steps: [
                {
                    title: 'Loan Approver Review',
                    description: 'Review loan details as a loan approver without editing recommendations',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanDetailsView', props: { fromContext: true } }],
                },
                {
                    title: 'Application Documents',
                    description: 'Review the uploaded loan application documents',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanDocumentsView', props: { fromContext: true } }],
                },
                {
                    title: 'Loan Schedule',
                    description: 'Review the loan repayment schedule based on the current effective values',
                    customComponents: [{ type: 'customComponent', componentName: 'LoanScheduleView', props: { fromContext: true, useRecommendedValues: true } }],
                },
                {
                    title: 'Decision',
                    customComponents: [{ type: 'customComponent', componentName: 'ApprovalDecision', props: { required: true, fromContext: true }, key: 'approvalDecision' }],
                },
            ],
        },
    },
};

// ---------------------------------------------------------------------------
// Node config → DataRenderConfig resolver (mirrors backend logic)
// ---------------------------------------------------------------------------
function resolveNodeConfig(nodeConfigObj: any, nodeName: string): any {
    if (nodeName && nodeConfigObj?.nodeViews?.[nodeName]) {
        return nodeConfigObj.nodeViews[nodeName];
    }
    if (nodeConfigObj?.defaultView) {
        return nodeConfigObj.defaultView;
    }
    return { steps: [] };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const FlowSimulatorView: React.FC = () => {
    const [configJson, setConfigJson] = useState(JSON.stringify(DEFAULT_NODE_CONFIG, null, 2));
    const [configError, setConfigError] = useState<string | null>(null);
    const [parsedConfig, setParsedConfig] = useState<any>(DEFAULT_NODE_CONFIG);
    const [selectedNode, setSelectedNode] = useState<string>('LOAN_OFFICER');
    const [showConfigPanel, setShowConfigPanel] = useState(false);
    const [showDataPanel, setShowDataPanel] = useState(true);
    const [simKey, setSimKey] = useState(0);

    // Loan scenario inputs
    const [loanAmount, setLoanAmount] = useState<number>(5000000);
    const [loanPeriod, setLoanPeriod] = useState<number>(24);
    const [employerName, setEmployerName] = useState<string>('');

    // Real data hook
    const {
        companyName,
        branch,
        activeProducts,
        isProductsLoading,
        customers,
        isSearching,
        customerSearch,
        setCustomerSearch,
        selectedCustNo,
        setSelectedCustNo,
        selectedGlNo,
        setSelectedGlNo,
        customer,
        loanProducts,
        buildAppapproDTO,
    } = useFlowSimulatorData();

    // When customer or product changes, sync default period/rate
    const selectedProduct = activeProducts.find(p => p.glNo === selectedGlNo);
    useEffect(() => {
        if (selectedProduct?.defaultTerm) setLoanPeriod(selectedProduct.defaultTerm);
    }, [selectedProduct?.defaultTerm]);

    // Build live AppapproDTO from real selections
    const liveAppapproDTO = useMemo(() => buildAppapproDTO({
        custNo: selectedCustNo,
        name: customer?.name,
        glNo: selectedGlNo,
        productName: selectedProduct?.productName,
        loanAmount,
        loanPeriod,
        intRate: selectedProduct?.baseInterestRate ?? 12,
        employerName,
        institutionName: companyName,
        branch,
    }), [buildAppapproDTO, selectedCustNo, customer, selectedGlNo, selectedProduct, loanAmount, loanPeriod, employerName, companyName, branch, simKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Available node names derived from the parsed config
    const nodeNames = useMemo(() => {
        const nodes = Object.keys(parsedConfig?.nodeViews || {});
        if (parsedConfig?.defaultView) nodes.unshift('(defaultView)');
        return nodes;
    }, [parsedConfig]);

    // Resolved step config for DynamicWorkflowForm
    const resolvedStepConfig = useMemo(() => {
        const node = selectedNode === '(defaultView)' ? '' : selectedNode;
        const view = resolveNodeConfig(parsedConfig, node);
        return {
            steps: view.steps || [],
            splitScreen: { enabled: false, fields: [] },
        };
    }, [parsedConfig, selectedNode]);

    // Initial form data — built from live real data
    const initialFormData = useMemo(() => ({
        ...MOCK_ACTION_FLOW_DATA,
        state: selectedNode === '(defaultView)' ? 'LOAN_OFFICER' : selectedNode,
        valueData: liveAppapproDTO,
        changesData: liveAppapproDTO,
        beforeData: liveAppapproDTO,
    }), [liveAppapproDTO, selectedNode, simKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Custom components — same registry as ActionFlowApprovalView
    const customComponents = useMemo(() => ({
        LoanDetailsView:                   (props: any) => <LoanDetailsViewWithProvider {...props} />,
        LoanOfficerView:                    (props: any) => <LoanOfficerViewWithProvider {...props} />,
        LoanScheduleView:                   (props: any) => <LoanScheduleView {...props} />,
        LoanGuarantorView:                  (props: any) => <LoanGuarantorView {...props} />,
        LoanDocumentsView:                  (props: any) => <LoanDocumentsView {...props} />,
        LoanOfficerRecommendationView:      (props: any) => <LoanOfficerRecommendationView {...props} />,
        LoanApplicationFormView:           (props: any) => <LoanApplicationFormView {...props} />,
        DeductionFormView:                  (props: any) => <DeductionFormView {...props} />,
        OfficerDeductionFormGenerationView: (props: any) => <OfficerDeductionFormGenerationView {...props} />,
        DocumentUploadView:                 (props: any) => <DocumentUploadView {...props} />,
        LoanSupervisorReviewView:           (props: any) => <LoanSupervisorReviewViewWithProvider {...props} />,
        ApprovalDecision:                   (props: any) => <ApprovalDecision {...props} />,
        ESSLoanDetailsView:                 (props: any) => <ESSLoanDetailsView {...props} />,
        ESSEmployeeInfoView:                (props: any) => <ESSEmployeeInfoView {...props} />,
        ESSFinancialInfoView:               (props: any) => <ESSFinancialInfoView {...props} />,
        ESSQualificationAdviceView:         (props: any) => <ESSQualificationAdviceView {...props} />,
        ESSTakeoverInfoView:                (props: any) => <ESSTakeoverInfoView {...props} />,
        ESSLoanSummaryView:                 (props: any) => <ESSLoanSummaryView {...props} />,
        ESSDebugView:                       (props: any) => <ESSDebugView {...props} />,
    }), []);

    // -----------------------------------------------------------------------
    // Handlers
    // -----------------------------------------------------------------------
    const handleConfigChange = useCallback((value: string) => {
        setConfigJson(value);
        try {
            const parsed = JSON.parse(value);
            setParsedConfig(parsed);
            setConfigError(null);
            const nodes = Object.keys(parsed?.nodeViews || {});
            if (nodes.length > 0 && !nodes.includes(selectedNode)) {
                setSelectedNode(nodes[0]);
            }
        } catch (e: any) {
            setConfigError(e.message);
        }
    }, [selectedNode]);

    const handleResetConfig = () => {
        const json = JSON.stringify(DEFAULT_NODE_CONFIG, null, 2);
        setConfigJson(json);
        setParsedConfig(DEFAULT_NODE_CONFIG);
        setConfigError(null);
        setSelectedNode('LOAN_OFFICER');
        message.success('Config reset to defaults');
    };

    const handleRun = () => {
        if (configError) {
            message.error('Fix JSON config errors before running');
            return;
        }
        if (!selectedCustNo) {
            message.warning('Select a customer before running');
            return;
        }
        if (!selectedGlNo) {
            message.warning('Select a loan product before running');
            return;
        }
        setSimKey(k => k + 1);
        message.success(`Simulating node: ${selectedNode}`);
    };

    const handleSimSubmit = (values: any) => {
        console.info('[FlowSimulator] Submitted values:', values);
        message.info('Submission captured — check browser console for full values');
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div style={{ padding: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Header */}
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>
                        <PlayCircleOutlined style={{ marginRight: 8 }} />
                        Flow Simulator
                    </Title>
                    <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Preview how any workflow node renders with real company, customer, and product data.
                        Select a customer and product, configure the loan parameters, then hit <strong>Run</strong>.
                    </Paragraph>
                    {companyName && (
                        <Tag icon={<BankOutlined />} color="blue" style={{ marginTop: 4 }}>
                            {companyName}
                        </Tag>
                    )}
                </div>

                {/* Loan Scenario Setup */}
                <Card
                    title={
                        <Space>
                            <UserOutlined />
                            <span>Loan Scenario</span>
                        </Space>
                    }
                    size="small"
                    extra={
                        <Button
                            size="small"
                            onClick={() => setShowDataPanel(v => !v)}
                        >
                            {showDataPanel ? 'Collapse' : 'Expand'}
                        </Button>
                    }
                >
                    {showDataPanel && (
                        <Form layout="vertical" size="small">
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label="Customer" required>
                                        <AutoComplete
                                            value={customerSearch}
                                            options={(customers || []).map(c => ({
                                                value: c.custNo || '',
                                                label: `${c.name} (${c.custNo})`,
                                            }))}
                                            onSearch={setCustomerSearch}
                                            onSelect={(val, opt) => {
                                                setSelectedCustNo(val);
                                                setCustomerSearch(opt.label as string);
                                            }}
                                            placeholder="Type name or customer no…"
                                            notFoundContent={isSearching ? <Spin size="small" /> : customerSearch.length < 2 ? 'Type at least 2 characters' : 'No customer found'}
                                            allowClear
                                            onClear={() => { setSelectedCustNo(''); setCustomerSearch(''); }}
                                        />
                                        {customer && (
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Branch: {customer.branch} | No: {customer.custNo}
                                            </Text>
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label="Loan Product" required>
                                        <Select
                                            value={selectedGlNo || undefined}
                                            onChange={setSelectedGlNo}
                                            placeholder={isProductsLoading ? 'Loading products…' : 'Select loan product'}
                                            loading={isProductsLoading}
                                            showSearch
                                            optionFilterProp="children"
                                            allowClear
                                            onClear={() => setSelectedGlNo('')}
                                        >
                                            {activeProducts.map(p => (
                                                <Option key={p.glNo} value={p.glNo}>
                                                    {p.productName} ({p.glNo})
                                                </Option>
                                            ))}
                                        </Select>
                                        {selectedProduct && (
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Rate: {selectedProduct.baseInterestRate}% | Term: {selectedProduct.minimumTerm}–{selectedProduct.maximumTerm} mo
                                            </Text>
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item label="Loan Amount (TZS)">
                                        <InputNumber
                                            value={loanAmount}
                                            onChange={v => setLoanAmount(v ?? 0)}
                                            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={v => Number((v || '').replace(/,/g, ''))}
                                            style={{ width: '100%' }}
                                            min={0}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item label="Period (months)">
                                        <InputNumber
                                            value={loanPeriod}
                                            onChange={v => setLoanPeriod(v ?? 1)}
                                            min={1}
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label="Employer Name">
                                        <Input
                                            value={employerName}
                                            onChange={e => setEmployerName(e.target.value)}
                                            placeholder="e.g. Ministry of Finance"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Card>

                {/* Node Controls */}
                <Card size="small">
                    <Row gutter={16} align="middle" wrap>
                        <Col flex="auto">
                            <Space wrap>
                                <Text strong>Node:</Text>
                                <Select
                                    value={selectedNode}
                                    onChange={setSelectedNode}
                                    style={{ minWidth: 240 }}
                                    placeholder="Select node to simulate"
                                >
                                    {nodeNames.map(n => (
                                        <Option key={n} value={n}>
                                            {n}
                                            {n === 'LOAN_OFFICER' || n === 'MEMBER_DEDUCTION_FORM' ? (
                                                <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>loan</Tag>
                                            ) : null}
                                        </Option>
                                    ))}
                                </Select>

                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={handleRun}
                                    disabled={!!configError}
                                >
                                    Run
                                </Button>

                                <Button
                                    icon={<CodeOutlined />}
                                    onClick={() => setShowConfigPanel(v => !v)}
                                >
                                    {showConfigPanel ? 'Hide' : 'Edit'} Node Config
                                </Button>
                            </Space>
                        </Col>

                        <Col>
                            <Space>
                                {configError && <Tag color="error">Config JSON error</Tag>}
                                {!configError && (
                                    <Tag color="success">
                                        {resolvedStepConfig.steps.length} step(s) loaded
                                    </Tag>
                                )}
                                {simKey > 0 && (
                                    <Tag color="processing">Run #{simKey}</Tag>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* Node Config editor */}
                {showConfigPanel && (
                    <Card
                        title="Node Config JSON"
                        size="small"
                        extra={
                            <Button size="small" icon={<ReloadOutlined />} onClick={handleResetConfig}>
                                Reset
                            </Button>
                        }
                    >
                        {configError && (
                            <Alert message={configError} type="error" showIcon style={{ marginBottom: 8 }} />
                        )}
                        <TextArea
                            value={configJson}
                            onChange={e => handleConfigChange(e.target.value)}
                            rows={20}
                            style={{ fontFamily: 'monospace', fontSize: 12 }}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Paste the full nodeConfig JSON (with defaultView / groupViews / nodeViews).
                        </Text>
                    </Card>
                )}

                <Divider style={{ margin: '8px 0' }}>
                    <Text type="secondary">
                        Simulating: <strong>{selectedNode}</strong>
                        {' '}({resolvedStepConfig.steps.length} steps)
                        {customer && <> — <strong>{customer.name}</strong></>}
                        {selectedProduct && <> · <strong>{selectedProduct.productName}</strong></>}
                    </Text>
                </Divider>

                {/* The actual simulated form */}
                {simKey === 0 ? (
                    <Alert
                        message="Ready to simulate"
                        description="Select a customer and loan product above, then click Run to start the simulation."
                        type="info"
                        showIcon
                    />
                ) : resolvedStepConfig.steps.length === 0 ? (
                    <Alert
                        message="No steps found"
                        description={`The selected node "${selectedNode}" has no steps configured. Check your node config JSON.`}
                        type="warning"
                        showIcon
                    />
                ) : (
                    <ReactFlowProvider>
                        <DynamicWorkflowForm
                            key={`sim-${selectedNode}-${simKey}`}
                            config={resolvedStepConfig}
                            initialData={initialFormData}
                            onSubmit={handleSimSubmit}
                            customComponents={customComponents}
                            hideSubmissionStep={false}
                        />
                    </ReactFlowProvider>
                )}
            </Space>
        </div>
    );
};

export default FlowSimulatorView;
